import styles from './catalog-picker-modal.module.css'

import { RxDotsHorizontal } from '@react-icons/all-files/rx/RxDotsHorizontal'
import { RxDrawingPinFilled } from '@react-icons/all-files/rx/RxDrawingPinFilled'
import { RxStarFilled } from '@react-icons/all-files/rx/RxStarFilled'
import React, { useMemo, useState } from 'react'

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  SearchTextField,
  Tabs,
  Text,
} from 'components/ds'

import { CatalogAvatar, CatalogList } from './catalog-list'
import { GroupEditor } from './group-editor'
import type { CatalogGroup, PickerCatalog } from './mock-catalogs'
import { SelectionBar } from './selection-bar'
import { filterCatalogs } from './utils'

export type CatalogSelection = {
  catalogIds: string[]
  groupIds: string[]
}

type CatalogPickerModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  catalogs: PickerCatalog[]
  groups: CatalogGroup[]
  onGroupsChange: (groups: CatalogGroup[]) => void
  selection: CatalogSelection
  onApply: (selection: CatalogSelection) => void
}

/**
 * Lets the user narrow a search to specific catalogs they can reach across
 * DISCO, and save sets of catalogs as reusable groups.
 */
export const CatalogPickerModal = ({
  open,
  onOpenChange,
  catalogs,
  groups,
  onGroupsChange,
  selection,
  onApply,
}: CatalogPickerModalProps) => {
  const [tab, setTab] = useState<'catalogs' | 'groups'>('catalogs')
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<CatalogSelection>(selection)
  const [editing, setEditing] = useState<CatalogGroup | null>(null)

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(selection)
      setQuery('')
      setEditing(null)
    }
    onOpenChange(next)
  }

  const visibleCatalogs = useMemo(
    () => filterCatalogs(catalogs, query),
    [catalogs, query],
  )

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? groups.filter(g => g.name.toLowerCase().includes(q)) : groups
  }, [groups, query])

  const toggle = (key: keyof CatalogSelection, id: string) =>
    setDraft(d => ({
      ...d,
      [key]: d[key].includes(id)
        ? d[key].filter(i => i !== id)
        : [...d[key], id],
    }))

  const saveGroup = (group: CatalogGroup) => {
    const exists = groups.some(g => g.id === group.id)
    onGroupsChange(
      exists
        ? groups.map(g => (g.id === group.id ? group : g))
        : // New groups go first so the user sees the one they just made.
          [group, ...groups],
    )
    // A new group stands in for the catalogs it was built from, so they
    // aren't counted twice in the selection. Switching to Groups shows where
    // those ticks went.
    if (!exists) {
      setDraft(d => ({
        catalogIds: d.catalogIds.filter(id => !group.catalogIds.includes(id)),
        groupIds: [...d.groupIds, group.id],
      }))
    }
    setEditing(null)
    setTab('groups')
  }

  const togglePin = (groupId: string) =>
    onGroupsChange(
      groups.map(g => (g.id === groupId ? { ...g, isPinned: !g.isPinned } : g)),
    )

  const deleteGroup = (groupId: string) => {
    onGroupsChange(groups.filter(g => g.id !== groupId))
    setDraft(d => ({ ...d, groupIds: d.groupIds.filter(i => i !== groupId) }))
    setEditing(null)
  }

  const selectedCount = draft.catalogIds.length + draft.groupIds.length
  const catalogById = (id: string) => catalogs.find(c => c.id === id)

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content maxWidth="520px" className={styles.content}>
        {editing ? (
          <GroupEditor
            group={editing}
            catalogs={catalogs}
            onBack={() => setEditing(null)}
            onSave={saveGroup}
            onDelete={deleteGroup}
          />
        ) : (
          <>
            <Dialog.Title mb="1">Search specific catalogs</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              Choose catalogs you have access to across DISCO, or save them as a
              group to reuse.
            </Dialog.Description>

            <Tabs.Root value={tab} onValueChange={v => setTab(v as typeof tab)}>
              <Tabs.List size="2">
                <Tabs.Trigger value="catalogs">
                  <span>Catalogs</span>
                  {draft.catalogIds.length > 0 && (
                    <span>&nbsp;({draft.catalogIds.length})</span>
                  )}
                </Tabs.Trigger>
                <Tabs.Trigger value="groups">
                  <span>Groups</span>
                  {draft.groupIds.length > 0 && (
                    <span>&nbsp;({draft.groupIds.length})</span>
                  )}
                </Tabs.Trigger>
              </Tabs.List>

              <Box mt="3">
                <SearchTextField
                  placeholder={
                    tab === 'catalogs' ? 'Search catalogs' : 'Search groups'
                  }
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onClear={() => setQuery('')}
                />
              </Box>

              <Tabs.Content value="catalogs">
                {draft.catalogIds.length > 0 && (
                  <SelectionBar
                    count={draft.catalogIds.length}
                    onClear={() => setDraft(d => ({ ...d, catalogIds: [] }))}
                    onSaveAsGroup={name =>
                      saveGroup({
                        id: `g${Date.now()}`,
                        name,
                        catalogIds: draft.catalogIds,
                      })
                    }
                  />
                )}
                <Box className={styles.list} mt="2">
                  <CatalogList
                    catalogs={visibleCatalogs}
                    selectedIds={draft.catalogIds}
                    onToggle={id => toggle('catalogIds', id)}
                  />
                </Box>
              </Tabs.Content>

              <Tabs.Content value="groups">
                {/* Same slot as the Catalogs selection bar. Groups are made
                    from a selection on the Catalogs tab, not here. */}
                <Flex
                  align="center"
                  justify="between"
                  className={
                    draft.groupIds.length > 0
                      ? styles.selectionBar
                      : styles.listBar
                  }
                >
                  {draft.groupIds.length > 0 ? (
                    <Flex align="center" gap="2">
                      <Text size="2" weight="medium">
                        {draft.groupIds.length} selected
                      </Text>
                      <Text size="2" color="gray" aria-hidden="true">
                        ·
                      </Text>
                      <Button
                        variant="ghost"
                        color="gray"
                        size="1"
                        onClick={() => setDraft(d => ({ ...d, groupIds: [] }))}
                      >
                        Clear
                      </Button>
                    </Flex>
                  ) : (
                    <Text size="2" color="gray">
                      {`${groups.length} ${
                        groups.length === 1 ? 'group' : 'groups'
                      }`}
                    </Text>
                  )}
                </Flex>
                <Box className={styles.list} mt="2">
                  {visibleGroups.length === 0 ? (
                    <Text size="2" color="gray" as="p" className={styles.empty}>
                      No groups match your search.
                    </Text>
                  ) : (
                    visibleGroups.map(group => (
                      <Flex
                        key={group.id}
                        align="center"
                        justify="between"
                        className={styles.row}
                      >
                        <Text as="label" size="2" className={styles.rowLabel}>
                          <Flex align="center" gap="3">
                            <Checkbox
                              checked={draft.groupIds.includes(group.id)}
                              onCheckedChange={() =>
                                toggle('groupIds', group.id)
                              }
                            />
                            <div className={styles.avatarStack}>
                              {group.catalogIds.slice(0, 4).map(id => (
                                <CatalogAvatar
                                  key={id}
                                  name={catalogById(id)?.name ?? '?'}
                                  logoUrl={catalogById(id)?.logoUrl}
                                />
                              ))}
                            </div>
                            <Flex direction="column" className={styles.rowText}>
                              <Flex align="center" gap="1">
                                {group.isFavourites && (
                                  <RxStarFilled
                                    aria-hidden="true"
                                    className={styles.favouriteIcon}
                                  />
                                )}
                                <Text weight="medium" truncate>
                                  {group.name}
                                </Text>
                                {group.isPinned && (
                                  <RxDrawingPinFilled
                                    aria-label="Pinned to quick select"
                                    className={styles.pinIcon}
                                  />
                                )}
                              </Flex>
                              <Text size="1" color="gray" truncate>
                                {group.catalogIds
                                  .map(id => catalogById(id)?.name)
                                  .filter(Boolean)
                                  .join(', ')}
                              </Text>
                            </Flex>
                          </Flex>
                        </Text>
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger>
                            <IconButton
                              variant="ghost"
                              color="gray"
                              size="1"
                              aria-label={`More actions for ${group.name}`}
                            >
                              <RxDotsHorizontal />
                            </IconButton>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content
                            align="end"
                            size="1"
                            className={styles.groupActions}
                          >
                            <DropdownMenu.Item
                              onSelect={() => togglePin(group.id)}
                            >
                              {group.isPinned
                                ? 'Unpin from quick select'
                                : 'Pin to quick select'}
                            </DropdownMenu.Item>
                            {/* Default groups come from DISCO, so only the
                                user's own groups can be changed. */}
                            {!group.isDefault && (
                              <>
                                <DropdownMenu.Separator />
                                <DropdownMenu.Item
                                  onSelect={() => setEditing(group)}
                                >
                                  Edit
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  color="red"
                                  onSelect={() => deleteGroup(group.id)}
                                >
                                  Delete
                                </DropdownMenu.Item>
                              </>
                            )}
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>
                      </Flex>
                    ))
                  )}
                </Box>
              </Tabs.Content>
            </Tabs.Root>

            <Flex justify="between" align="center" mt="4" gap="3">
              <Text size="1" color="gray">
                {selectedCount} selected
              </Text>
              <Flex gap="3">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button
                  onClick={() => {
                    onApply(draft)
                    onOpenChange(false)
                  }}
                >
                  Apply
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
